<template>
  <!-- HorseProfile.vue's <component> hardcodes class="tab-pane row mx-0 show active" for every
       tab (no per-tab wrapperClass) — a bare Bootstrap `.row` on this section would flex-wrap its
       children to content width instead of stacking full-width blocks (breaks the table + footer
       layout). HorseProfileDetails/Score/BoyHistory each compensate with their own inner `.col`;
       mirror that here rather than changing the shared template for every existing tab. -->
  <div class="col px-0 pr-lg-3">
  <section class="bg-white rounded mb-4 p-3 p-sm-4">
    <h1 class="h2 mb-3">Members</h1>
    <p class="text-muted">
      Members who own or ride this horse, or who are on the same account as someone who does.
      <span class="badge alert-prim">Owner</span> / <span class="badge alert-secondary">Rider</span>
      mark a member's own direct link to the horse; <span class="badge alert-dark">Family</span>
      means they can ride it only because a family member is its direct owner/rider.
    </p>

    <filter-bar :active="hasActiveFilters" @clear="clearAll">
      <filter-multiselect
        label="Zone"
        option-label="code"
        placeholder="Any zone"
        :value="filters.zone_id"
        :options="zoneOptions"
        @input="ids => setFilter('zone_id', ids)"
        @clear="clearFilter('zone_id')"
      />
      <filter-multiselect
        label="Status"
        placeholder="Any status"
        :value="filters.status_id"
        :options="riderStatusOptions"
        @input="ids => setFilter('status_id', ids)"
        @clear="clearFilter('status_id')"
      />
      <filter-multiselect
        label="BoY Status"
        placeholder="Any BoY status"
        :value="filters.boy_status_id"
        :options="riderStatusOptions"
        @input="ids => setFilter('boy_status_id', ids)"
        @clear="clearFilter('boy_status_id')"
      />
      <horse-member-relation-filter
        :value="filters.relation"
        @input="ids => setFilter('relation', ids)"
        @clear="clearFilter('relation')"
      />
    </filter-bar>

    <div class="form-inline mb-sm-3 d-flex justify-content-end">
      <div class="form-group col-12 col-lg-auto mt-2 mt-lg-0 ml-auto px-0 d-flex align-items-center">
        <label for="horseMembersSearch" class="d-none d-lg-inline mr-xl-2 mb-0 pr-2 font-weight-normal">Search</label>
        <div class="input-append d-flex flex-grow-1 flex-md-grow-0 mr-md-2">
          <input
            id="horseMembersSearch"
            v-model="searchInput"
            type="text"
            class="form-control mb-0 flex-grow-1 flex-md-grow-0"
            @keyup.enter="applySearch"
            @search="applySearch"
          >
          <svg
            class="append"
            role="button"
            aria-label="Search"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 14 14"
            width="16"
            height="16"
            @click="applySearch"
          >
            <rect width="100%" height="100%" fill="none" />
            <g>
              <path fill="#231815" d="M5.5 11C2.5 11 0 8.5 0 5.5S2.5 0 5.5 0 11 2.5 11 5.5 8.5 11 5.5 11zm0-10C3 1 1 3 1 5.5S3 10 5.5 10 10 8 10 5.5 8 1 5.5 1z" />
              <path fill="#231815" d="M9.5 8.8l4.4 4.4c.2.2.2.5 0 .7l-.4.1-.4-.1-4.4-4.4.8-.7z" />
            </g>
          </svg>
        </div>
      </div>
    </div>

    <div class="position-relative" style="min-height: 240px">
      <responsive-table
        :columns="columns"
        :rows="rows"
        :loading="loading"
        :breakpoints="breakpoints"
        :sort="filters.sort"
        :direction="filters.direction"
        empty-text="No members found."
        @sort="setSort"
      >
        <template #cell:first_name="{ row }">
          <router-link :to="{ name: 'Member Profile', params: { slug: row.id } }">{{ row.first_name }}</router-link>
        </template>
        <template #cell:last_name="{ row }">
          <list-name-cell
            align="right"
            :label="row.last_name"
            :to="{ name: 'Member Profile', params: { slug: row.id } }"
          >
            <template #badges>
              <span v-if="row.is_primary" class="badge alert-prim">Primary</span>
              <span class="badge" :class="statusBadgeClass(row)">{{ statusBadgeLabel(row) }}</span>
              <horse-member-relation-badges :is-owner="row.is_owner" :is-rider="row.is_rider" />
            </template>
          </list-name-cell>
        </template>
        <!-- Mobile/accordion breakpoint (see BREAKPOINTS below) summarises each row under a
             synthetic 'name' key — mirrors Members/Listing/Members.vue's #cell:name exactly, since
             there's no single 'name' column on this list (first/last are separate desktop columns). -->
        <template #cell:name="{ row }">
          <list-name-cell
            :label="`${row.first_name} ${row.last_name}`"
            :to="{ name: 'Member Profile', params: { slug: row.id } }"
          >
            <template #badges>
              <span v-if="row.is_primary" class="badge alert-prim">Primary</span>
              <span class="badge" :class="statusBadgeClass(row)">{{ statusBadgeLabel(row) }}</span>
              <horse-member-relation-badges :is-owner="row.is_owner" :is-rider="row.is_rider" />
            </template>
          </list-name-cell>
        </template>
        <template #cell:zone_id="{ row }">{{ zoneCode(row.zone_id) }}</template>
        <template #cell:beginning_of_year_status="{ row }">{{ row.beginning_of_year_status }}</template>
      </responsive-table>
    </div>

    <!-- Same markup/classes as the legacy DataTable footer (DataTableFields/ShowEntries.vue) -->
    <div class="form-inline justify-content-between align-items-start mt-3">
      <div class="d-flex align-items-center justify-content-center justify-content-md-start form-group mb-3 mb-md-0 col-12 col-md-auto px-0">
        <label for="horseMembersShow" class="mr-2 mb-0 font-weight-normal">Show</label>
        <select
          id="horseMembersShow"
          class="form-control mr-2"
          :value="filters.per_page"
          @change="setPerPage(Number($event.target.value))"
        >
          <option v-for="n in perPageOptions" :key="n" :value="n">{{ n }}</option>
        </select>
        <span>Entries</span>
      </div>

      <div class="pagination-container form-group align-items-start justify-content-center mb-3 mb-md-0" v-show="totalPages > 1">
        <pagination
          :total-pages="totalPages"
          :page="page"
          :on-change="setPage"
          :disabled="loading"
        />
      </div>
    </div>
  </section>
  </div>
</template>

<script>
import { defineComponent, computed, ref, watch, onMounted, onBeforeUnmount, getCurrentInstance } from '@vue/composition-api'
import ResponsiveTable from '@/components/ResponsiveTable'
import Pagination from '@/components/Pagination'
import FilterBar from '@/components/filters/FilterBar'
import FilterMultiselect from '@/components/filters/FilterMultiselect'
import HorseMemberRelationFilter from '@/components/filters/HorseMemberRelationFilter'
import HorseMemberRelationBadges from '@/components/HorseMemberRelationBadges'
import ListNameCell from '@/components/ListNameCell'
import useMembersList from '@/composables/members/useMembersList'

const COLUMNS = [
  { key: 'first_name', label: 'First Name', sortable: true, minWidth: '100px', width: '1.3fr' },
  { key: 'last_name', label: 'Last Name', sortable: true, minWidth: '150px', width: '2.4fr' },
  { key: 'member_number', label: 'Member No.', minWidth: '90px', width: '1.2fr' },
  { key: 'zone_id', label: 'Zone', sortable: true, minWidth: '60px', width: '0.6fr' },
  { key: 'beginning_of_year_status', label: 'BoY Status', sortable: true, minWidth: '90px', width: '1.3fr' }
]

const BREAKPOINTS = [
  {
    maxWidth: 700,
    hideHeader: true,
    accordion: {
      summary: [{ keys: ['name'], hideLabel: true }]
    },
    rows: [['member_number'], ['zone_id'], ['beginning_of_year_status']]
  }
]

export default defineComponent({
  name: 'HorseProfileMembers',
  components: { ResponsiveTable, Pagination, FilterBar, FilterMultiselect, HorseMemberRelationFilter, HorseMemberRelationBadges, ListNameCell },
  setup() {
    const { proxy } = getCurrentInstance()
    const store = proxy.$store

    // Mirrors HorseProfileBoyHistory.vue / HorseProfileScore.vue on this same page — the horse id
    // comes from the route, not a prop (HorseProfile.vue's <component> binds no props at all).
    const horseId = computed(() => proxy.$route.params.slug)
    const {
      rows, loading, page, totalPages, filters, perPageOptions,
      fetch, setPage, setSort, setPerPage, setSearch, setFilter, clearFilter, clearFilters
    } = useMembersList(horseId)

    const columns = COLUMNS
    const breakpoints = BREAKPOINTS

    const zoneOptions = computed(() => store.state.listZones || [])
    const riderStatusOptions = computed(() => store.state.riderStatus || [])

    const hasActiveFilters = computed(() =>
      Boolean(filters.search)
      || filters.zone_id.length > 0
      || filters.status_id.length > 0
      || filters.boy_status_id.length > 0
      || filters.relation.length > 0
    )

    const searchInput = ref(filters.search || '')

    function zoneCode(id) {
      const zone = (store.state.listZones || []).find(z => z.id === id)
      return zone ? zone.code : 'Unspecified'
    }

    // Mirrors Members.vue's statusBadgeLabel/Class (legacy Text.vue `showActive` badge).
    function statusBadgeLabel(row) {
      if (row.membership_status !== 'Active') return row.membership_status === 'Onhold' ? 'On Hold' : row.membership_status
      return row.financial ? 'Financial' : 'Unfinancial'
    }
    function statusBadgeClass(row) {
      if (row.membership_status !== 'Active') {
        if (row.membership_status === 'Lifetime') return 'alert-info'
        if (row.membership_status === 'Archived') return 'alert-dark'
        return 'alert-danger'
      }
      return row.financial ? 'alert-info' : 'alert-warning'
    }

    let searchTimer = null
    function applySearch() {
      clearTimeout(searchTimer)
      if (searchInput.value !== filters.search) setSearch(searchInput.value)
    }
    function clearAll() {
      clearTimeout(searchTimer)
      searchInput.value = ''
      clearFilters()
    }

    watch(searchInput, (val) => {
      clearTimeout(searchTimer)
      searchTimer = setTimeout(() => {
        if (val !== filters.search) setSearch(val)
      }, 500)
    })
    onBeforeUnmount(() => clearTimeout(searchTimer))

    onMounted(fetch)

    return {
      columns,
      breakpoints,
      rows,
      loading,
      page,
      totalPages,
      filters,
      perPageOptions,
      searchInput,
      zoneOptions,
      riderStatusOptions,
      hasActiveFilters,
      setPage,
      setSort,
      setPerPage,
      setFilter,
      clearFilter,
      clearAll,
      applySearch,
      zoneCode,
      statusBadgeLabel,
      statusBadgeClass
    }
  }
})
</script>



// WEBPACK FOOTER //
// src/views/Horses/Tabs/Members.vue